import React from 'react';
import { Link } from 'react-router-dom';
import { PopupButton } from 'react-calendly';
import {
  FaRobot,
  FaChartLine,
  FaBrain,
  FaCode,
  FaDatabase,
  FaUserCog,
  FaArrowRight,
  FaCheck,
  FaCogs,
  FaCloud
} from 'react-icons/fa';

const mainServices = [
  {
    icon: <FaRobot />,
    title: 'AI Consulting',
    description: 'Strategic guidance on implementing AI solutions to transform your business operations and drive growth.',
    features: [
      'AI Readiness Assessment',
      'Digital Transformation Strategy',
      'ROI Analysis',
      'Implementation Roadmap'
    ]
  },
  {
    icon: <FaChartLine />,
    title: 'Machine Learning Solutions',
    description: 'Custom ML models to analyze data, predict trends, and automate decision-making processes.',
    features: [
      'Predictive Analytics',
      'Pattern Recognition',
      'Automated Decision Systems',
      'Performance Optimization'
    ]
  },
  {
    icon: <FaBrain />,
    title: 'Natural Language Processing',
    description: 'Advanced NLP solutions for chatbots, content analysis, and automated communication systems.',
    features: [
      'Chatbot Development',
      'Text Analysis',
      'Sentiment Analysis',
      'Language Understanding'
    ]
  }
];

const additionalServices = [
  {
    icon: <FaRobot />,
    title: 'AutoWave Pro',
    description: 'Our flagship AI agent platform that combines web browsing, code editing, document creation, and advanced analytics in one powerful solution.',
    features: [
      'Agentic Web Browsing',
      'AI Code Editor',
      'Document Generation',
      'SEO Optimization',
      'Data Analysis',
      'Deep Research'
    ],
    link: 'https://www.autowave.pro',
    isProduct: true
  },
  {
    icon: <FaCode />,
    title: 'AI Development',
    description: 'End-to-end development of AI-powered applications and systems tailored to your needs.',
    features: [
      'Custom AI Applications',
      'API Integration',
      'System Architecture',
      'Quality Assurance'
    ]
  },
  {
    icon: <FaDatabase />,
    title: 'Data Analytics',
    description: 'Comprehensive data analysis services to uncover insights and drive informed decision-making.',
    features: [
      'Data Mining',
      'Statistical Analysis',
      'Visualization',
      'Reporting Systems'
    ]
  },
  {
    icon: <FaUserCog />,
    title: 'AI Integration',
    description: 'Seamless integration of AI solutions with your existing systems and workflows.',
    features: [
      'System Integration',
      'Workflow Automation',
      'Legacy System Updates',
      'Performance Monitoring'
    ]
  }
];

const processSteps = [
  {
    icon: <FaCogs />,
    title: 'Assessment',
    description: 'We analyze your business needs and identify opportunities for AI implementation.'
  },
  {
    icon: <FaBrain />,
    title: 'Strategy',
    description: 'Develop a comprehensive strategy and roadmap for AI integration.'
  },
  {
    icon: <FaCode />,
    title: 'Development',
    description: 'Build and implement custom AI solutions tailored to your needs.'
  },
  {
    icon: <FaCloud />,
    title: 'Deployment',
    description: 'Deploy solutions and provide ongoing support and optimization.'
  }
];

const Services = () => {
  return (
    <div className="min-h-screen bg-nvidia-dark">
      {/* Hero Section */}
      <section className="bg-white py-32">
        <div className="container mx-auto px-6 pt-8">
          <div className="text-center mb-20">
            <h1 className="text-4xl md:text-5xl nvidia-heading mb-8 text-black">
              Our <span className="text-nvidia-green">Services</span>
            </h1>
            <p className="text-lg nvidia-body text-black max-w-3xl mx-auto mb-8 leading-relaxed">
              Ready to boost your profits without the headache? Our AI solutions seamlessly integrate into your existing business processes in weeks, not months. We specialize in quick implementations that start delivering ROI from day one.
            </p>
            <p className="text-lg nvidia-body text-black max-w-3xl mx-auto mb-12 leading-relaxed">
              From automating repetitive tasks to uncovering hidden revenue opportunities, we help you do more with less. Our clients typically see <span className="nvidia-subheading text-nvidia-green">30-50% cost reduction</span> and <span className="nvidia-subheading text-nvidia-green">2-3x productivity gains</span> within the first three months. Let's transform your business challenges into profit opportunities – without the usual stress and complexity.
            </p>
            <div className="max-w-5xl mx-auto mt-16 -mb-16">
              <img
                src="/images/services/ai pic 22.png"
                alt="Botrex AI Services Overview"
                className="w-full h-auto rounded-lg shadow-2xl hover:shadow-3xl transition-shadow duration-300"
                style={{
                  imageRendering: 'high-quality',
                  WebkitFontSmoothing: 'antialiased',
                  objectFit: 'cover',
                  filter: 'contrast(1.08) saturate(1.08) brightness(1.02) sharpen(1.5)',
                  transform: 'translateZ(0)',
                  backfaceVisibility: 'hidden',
                  maxHeight: '600px'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="bg-white pt-0 pb-16">
        <div className="container mx-auto px-6 -mt-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mainServices.map((service, index) => (
              <div
                key={index}
                className="p-6 rounded-lg bg-nvidia-dark-800 transform hover:-translate-y-1 transition-transform duration-300 border border-nvidia-dark-600"
              >
                <div className="w-12 h-12 bg-nvidia-dark-700 rounded-lg flex items-center justify-center mb-4 text-nvidia-green">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">{service.title}</h3>
                <p className="text-lg nvidia-body text-nvidia-gray-400 mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <FaCheck className="text-nvidia-green mt-1.5 mr-2" />
                      <span className="text-lg nvidia-body text-nvidia-gray-400">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Process</h2>
            <p className="text-lg nvidia-body text-gray-400">
              A streamlined approach to implementing AI solutions
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div
                key={index}
                className="text-center p-6 bg-nvidia-dark-800 rounded-lg border border-nvidia-green/20"
              >
                <div className="text-nvidia-green text-4xl mb-4">{step.icon}</div>
                <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                <p className="text-lg nvidia-body text-gray-400">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-nvidia-dark">Additional Services</h2>
            <p className="text-lg nvidia-body text-nvidia-gray-600">
              Explore our comprehensive range of specialized AI solutions
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <div
                key={index}
                className="p-6 rounded-lg bg-nvidia-dark-800 transform hover:-translate-y-1 transition-transform duration-300 border border-nvidia-dark-600"
              >
                <div className="w-12 h-12 bg-nvidia-dark-700 rounded-lg flex items-center justify-center mb-4 text-nvidia-green">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">{service.title}</h3>
                <p className="text-lg nvidia-body text-nvidia-gray-400 mb-4">{service.description}</p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <FaCheck className="text-nvidia-green mt-1.5 mr-2" />
                      <span className="text-lg nvidia-body text-nvidia-gray-400">{feature}</span>
                    </li>
                  ))}
                </ul>
                {service.isProduct && (
                  <a
                    href={service.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-nvidia-green text-black px-4 py-2 rounded-lg font-semibold hover:bg-opacity-90 transition-colors"
                  >
                    Try AutoWave Pro <FaArrowRight className="ml-2" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="bg-nvidia-dark-800 rounded-lg p-12 text-center">
            <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-lg nvidia-body text-gray-400 mb-8">
              Let's discuss how our AI solutions can transform your business
            </p>
            <PopupButton
              url="https://calendly.com/rahwsty/30min"
              rootElement={document.getElementById('root')}
              text="Schedule a Consultation"
              className="bg-nvidia-green text-black px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
